/**
 * LeadCockpit — tela cheia de relacionamento de UM lead (/orcamentos/leads/:id).
 *
 * Alinhado ao metodo SPIN Selling: guia o diagnostico (S/P/I/N), captura as
 * respostas do cliente em notas categorizadas, centraliza timeline/contatos,
 * tarefas, anexos, compromissos, orcamentos vinculados, dados editaveis e
 * acoes rapidas (WhatsApp, e-mail, registrar contato, criar orcamento).
 *
 * Layout em 3 zonas (padrao Bloco D + cockpit):
 *   1. CockpitHeader (EntityHeader: voltar, titulo+numero, badges, criar orc.)
 *   2. CockpitKpis (KpiStrip: dias pipeline/etapa, contatos, follow-up, valor,
 *      SPIN health)
 *   3. Grid 2 colunas: esquerda = blocos empilhados (timeline, SPIN, notas,
 *      tarefas, orcamentos, anexos, compromissos); direita = sidebar (acoes
 *      rapidas, dados do lead, responsavel).
 *
 * Todas as mutacoes recarregam o lead detalhe (fonte unica). AsyncButton
 * cuida dos toasts; erros de request tem auto-toast global (AuthContext).
 *
 * Gates (CRM-23): cada controle segue a permissao do endpoint que ele chama —
 * lead:edit para escrever no lead e nas sub-colecoes, lead:perder para
 * registrar/reverter perda, lead:mover + regra do pipeline para mudar etapa
 * (utils/crmPermissoes). Quem so ve o lead ve o cockpit em leitura.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiCalendar,
  FiMail,
  FiMessageCircle,
  FiPhone,
  FiSend,
  FiUser,
  FiXCircle,
} from "react-icons/fi";

import Block from "../../components/Block";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  arquivarLead,
  atribuirLead,
  atualizarLead,
  atualizarNota,
  atualizarSubtarefa,
  atualizarTarefa,
  atualizarCompromisso,
  criarCompromisso,
  criarNota,
  criarOrcamentoDoLead,
  criarSubtarefa,
  criarTarefa,
  desarquivarLead,
  enviarEmailLead,
  excluirCompromisso,
  excluirLead,
  excluirNota,
  excluirSubtarefa,
  excluirTarefa,
  fixarNota,
  marcarCompromissoRealizado,
  moverLead,
  obterLead,
  obterPipeline,
  obterTimeline,
  comentarRegistroTimeline,
  perderLead,
  reativarLead,
  registrarAtividade,
  toggleSubtarefa,
  toggleTarefa,
} from "../../services/crm";
import { PageContainer } from "../../styles/shared";
import { leadEncerrado, podeMoverLead } from "../../utils/crmPermissoes";
import { linkWhatsapp } from "../../utils/leadFormat";
import { contatoDoLead } from "./cockpit/contatoLead";

import CockpitHeader from "./cockpit/CockpitHeader";
import CockpitKpis from "./cockpit/CockpitKpis";
import TimelineHistorico from "./cockpit/TimelineHistorico";
import DiagnosticoSpin from "./cockpit/DiagnosticoSpin";
import NotasGerais from "./cockpit/NotasGerais";
import TarefasBlock from "./cockpit/TarefasBlock";
import OrcamentosBlock from "./cockpit/OrcamentosBlock";
import AnexosBlock from "./cockpit/AnexosBlock";
import RespostasFormularioBlock from "./cockpit/RespostasFormularioBlock";
import EnviarFormularioDialog from "./cockpit/EnviarFormularioDialog";
import CompromissosBlock from "./cockpit/CompromissosBlock";
import DadosLeadBlock from "./cockpit/DadosLeadBlock";
import ResponsavelBlock from "./cockpit/ResponsavelBlock";
import EnviarEmailDialog from "./cockpit/EnviarEmailDialog";
import NovoCompromissoDialog from "./cockpit/NovoCompromissoDialog";
import CriarOrcamentoDialog from "./cockpit/CriarOrcamentoDialog";
import MoverEtapaDialog from "./cockpit/MoverEtapaDialog";
import RegistrarAtividadeDialog from "./cockpit/RegistrarAtividadeDialog";
import RegistrarPerdaDialog from "./cockpit/RegistrarPerdaDialog";
import { CockpitGrid, LeftRail, MainCol } from "./cockpit/styles";
import { EmptyMsg, ErrorMsg } from "../Orcamentos/styles";

// Monta um texto de contexto SPIN (problema/implicacao/necessidade) para
// pre-preencher e-mail/WhatsApp/observacoes do orcamento — o coracao do
// metodo: a proposta usa as dores/ganhos verbalizados pelo cliente.
function montarContextoSpin(spin) {
  if (!spin) return "";
  const blocos = [];
  const add = (titulo, lista) => {
    if (Array.isArray(lista) && lista.length) {
      blocos.push(
        `${titulo}:\n` + lista.map((n) => `- ${n.texto}`).join("\n")
      );
    }
  };
  add("Problemas levantados", spin.PROBLEMA);
  add("Implicacoes", spin.IMPLICACAO);
  add("Necessidades", spin.NECESSIDADE);
  return blocos.join("\n\n");
}

function LeadCockpit() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { request, hasAllPermissions, hasPermission, isMaster } = useAuth();
  const toast = useToast();

  const [lead, setLead] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [etapasMap, setEtapasMap] = useState({}); // id -> nome (badge da etapa)
  // Regras do pipeline: "vendedor pode mover" decide, com lead:mover e
  // pipeline:configurar, se "Mover etapa" aparece (mesmo portao do board).
  const [regrasPipeline, setRegrasPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialogs (SlideOverPanel)
  const [dialog, setDialog] = useState(null);
  // 'whatsapp' | 'email' | 'compromisso' | 'orcamento' | 'mover' | 'atividade'

  const podeEditar = isMaster || hasAllPermissions(["lead:edit"]);
  const podeCriarOrcamento =
    isMaster || hasAllPermissions(["lead:edit", "orcamento:create"]);
  // R22: excluir lead eh hard delete — gate forte (lead:delete). O backend
  // ainda exige MFA recente (step-up tratado pelo interceptor global).
  const podeExcluir = isMaster || hasAllPermissions(["lead:delete"]);
  // Registrar perda / reativar — permissao nova (concedivel nos overrides).
  const podeRegistrarPerda = isMaster || hasAllPermissions(["lead:perder"]);
  const podeMover = podeMoverLead(hasPermission, regrasPipeline, isMaster);
  // FORM-12: "Preencher agora" no dialogo de formulario e' o consumidor da
  // permissao formulario:responder (o backend ainda exige lead:edit).
  const podePreencherFormulario = isMaster || hasPermission("formulario:responder");
  const [respostasVersao, setRespostasVersao] = useState(0);

  const voltar = useCallback(() => navigate("/crm"), [navigate]);

  // ---------- Carregar lead + timeline ----------
  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const detalhe = await obterLead(request, leadId);
      setLead(detalhe);
      setError(null);
      // Timeline em request separada (fail silencioso — nao quebra a tela).
      try {
        const tl = await obterTimeline(request, leadId);
        setTimeline(Array.isArray(tl) ? tl : []);
      } catch {
        setTimeline([]);
      }
    } catch (err) {
      setError(err?.message || "Erro ao carregar o lead.");
      setLead(null);
    } finally {
      setLoading(false);
    }
  }, [request, leadId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Mapa etapa.id -> nome para o badge de etapa no header (o detalhe do lead
  // so traz o id da etapa) + regras do pipeline para o gate de mover.
  // obterPipeline cai num default seguro em erro.
  useEffect(() => {
    obterPipeline(request)
      .then((p) => {
        const mapa = {};
        (Array.isArray(p?.etapas) ? p.etapas : []).forEach((e) => {
          mapa[e.id] = e.nome;
        });
        setEtapasMap(mapa);
        setRegrasPipeline(p?.regras || {});
      })
      .catch(() => {
        setEtapasMap({});
        setRegrasPipeline({});
      });
  }, [request]);

  const fecharDialog = useCallback(() => setDialog(null), []);

  // ---------- Helper: roda mutacao + recarrega ----------
  // Os AsyncButton/inline handlers chamam estes; recarregam o detalhe (fonte
  // unica) para refletir notas/tarefas/metricas/timeline atualizadas.
  const aposMutacao = useCallback(async () => {
    await carregar();
  }, [carregar]);

  // ---------- Handlers de acoes ----------

  const handleRegistrarAtividade = async (dados) => {
    await registrarAtividade(request, leadId, dados);
    await aposMutacao();
  };

  // Observacao presa a um registro da timeline: o endpoint ja devolve a
  // timeline atualizada, entao trocamos a lista sem recarregar o lead.
  const handleComentarTimeline = async (item, texto) => {
    const tl = await comentarRegistroTimeline(request, leadId, {
      origem: item.origem, registro_id: item.id, texto,
    });
    setTimeline(Array.isArray(tl) ? tl : []);
  };

  const handleArquivar = async () => {
    if (!lead) return;
    try {
      if (lead.arquivado) {
        await desarquivarLead(request, leadId);
        toast.success("Lead desarquivado.");
      } else {
        await arquivarLead(request, leadId);
        toast.success("Lead arquivado.");
      }
      await aposMutacao();
    } catch {
      /* auto-toast global ja avisou */
    }
  };

  const handleExcluir = async () => {
    if (!lead) return;
    // Acao destrutiva IRREVERSIVEL (hard delete). Confirmacao explicita +
    // sugestao de Arquivar no lugar (preserva historico).
    const ok = window.confirm(
      "Excluir este lead PERMANENTEMENTE? Esta ação não pode ser desfeita. "
      + "Para preservar o histórico, prefira Arquivar.",
    );
    if (!ok) return;
    try {
      await excluirLead(request, leadId);
      toast.success("Lead excluído.");
      navigate("/crm");
    } catch {
      /* auto-toast global ja avisou (MFA step-up / erro de vinculo) */
    }
  };

  // Perda: acao do vendedor. Reversivel (lead vai para a aba "Perdidos").
  // Lanca em erro para o dialog tratar inline; o sucesso recarrega o lead.
  const handlePerder = async ({ motivo, detalhe }) => {
    await perderLead(request, leadId, { motivo, detalhe });
    toast.success("Perda registrada.");
    await aposMutacao();
  };

  const handleReativar = async () => {
    if (!lead) return;
    try {
      await reativarLead(request, leadId);
      toast.success("Lead reativado.");
      await aposMutacao();
    } catch {
      /* auto-toast global ja avisou */
    }
  };

  // Semantica nova: mover e um move direto, sem campos extras.
  const handleMover = async (etapaDestinoId) => {
    await moverLead(request, leadId, { etapa_destino: etapaDestinoId });
    fecharDialog();
    await aposMutacao();
  };

  const handleAtribuir = async (responsavelId) => {
    await atribuirLead(request, leadId, responsavelId);
    await aposMutacao();
  };

  const handleSalvarDados = async (campos) => {
    await atualizarLead(request, leadId, campos);
    await aposMutacao();
  };

  // SPIN + notas gerais
  const handleAdicionarNotaSpin = async (categoria, texto) => {
    await criarNota(request, { lead: leadId, categoria, texto });
    await aposMutacao();
  };
  const handleAdicionarNotaGeral = async (texto) => {
    await criarNota(request, { lead: leadId, categoria: "GERAL", texto });
    await aposMutacao();
  };
  const handleEditarNota = async (id, texto) => {
    await atualizarNota(request, id, { texto });
    await aposMutacao();
  };
  const handleFixarNota = async (id, fixada) => {
    await fixarNota(request, id, fixada);
    await aposMutacao();
  };
  const handleExcluirNota = async (id) => {
    await excluirNota(request, id);
    await aposMutacao();
  };

  // Tarefas + subtarefas
  const handleCriarTarefa = async (dados) => {
    await criarTarefa(request, { lead: leadId, ...dados });
    await aposMutacao();
  };
  const handleToggleTarefa = async (id, concluida) => {
    await toggleTarefa(request, id, concluida);
    await aposMutacao();
  };
  const handleAtualizarTarefa = async (id, campos) => {
    await atualizarTarefa(request, id, campos);
    await aposMutacao();
  };
  const handleExcluirTarefa = async (id) => {
    await excluirTarefa(request, id);
    await aposMutacao();
  };
  const handleCriarSubtarefa = async (tarefaId, titulo) => {
    await criarSubtarefa(request, tarefaId, { titulo });
    await aposMutacao();
  };
  const handleToggleSubtarefa = async (id, concluida) => {
    await toggleSubtarefa(request, id, concluida);
    await aposMutacao();
  };
  const handleAtualizarSubtarefa = async (id, campos) => {
    await atualizarSubtarefa(request, id, campos);
    await aposMutacao();
  };
  const handleExcluirSubtarefa = async (id) => {
    await excluirSubtarefa(request, id);
    await aposMutacao();
  };

  // Compromissos
  const handleCriarCompromisso = async (dados) => {
    await criarCompromisso(request, { lead: leadId, ...dados });
    await aposMutacao();
  };
  // CRM-25: remarcar pelo mesmo dialog em modo edicao (PATCH).
  const [compromissoEmEdicao, setCompromissoEmEdicao] = useState(null);
  const abrirRemarcar = (compromisso) => {
    setCompromissoEmEdicao(compromisso);
    setDialog("compromisso");
  };
  const handleAtualizarCompromisso = async (id, campos) => {
    await atualizarCompromisso(request, id, campos);
    await aposMutacao();
  };
  const handleRealizarCompromisso = async (id, realizado) => {
    await marcarCompromissoRealizado(request, id, realizado);
    await aposMutacao();
  };
  const handleExcluirCompromisso = async (id) => {
    await excluirCompromisso(request, id);
    await aposMutacao();
  };

  // Acoes rapidas (dialogs)
  const handleEnviarEmail = async ({ assunto, corpo }) => {
    await enviarEmailLead(request, leadId, { assunto, corpo });
    toast.success("E-mail enviado.");
    await aposMutacao();
  };
  // Cria e RETORNA o orcamento; a navegacao (com state.from para voltar ao
  // cockpit) acontece no proprio CriarOrcamentoDialog.
  // O payload vem pronto do dialogo: `cliente_id` OU `tipo` + contato
  // (Transition Form — CRM-14/18) e `parceiros`.
  const handleCriarOrcamento = async (payload) => {
    const orcamento = await criarOrcamentoDoLead(request, leadId, payload);
    toast.success("Orçamento criado.");
    return orcamento;
  };

  const contextoSpin = useMemo(
    () => montarContextoSpin(lead?.spin),
    [lead?.spin]
  );

  // ---------- Estados de carregamento / erro ----------
  if (loading && !lead) {
    return (
      <PageContainer>
        <EmptyMsg>Carregando lead...</EmptyMsg>
      </PageContainer>
    );
  }
  if (error || !lead) {
    return (
      <PageContainer>
        <ErrorMsg>{error || "Lead não encontrado."}</ErrorMsg>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={voltar}
          style={{ marginTop: 12 }}
        >
          Voltar para o CRM
        </button>
      </PageContainer>
    );
  }

  // Nome da etapa: o detalhe traz so o `etapa` (id); resolvemos pelo mapa do
  // pipeline. Fallback discreto enquanto o mapa nao carregou.
  const etapaNome = etapasMap[lead.etapa] || null;
  // CRM-07: ganho e perdido nao mudam de etapa — o backend recusa os dois.
  const encerrado = leadEncerrado(lead);
  // CRM-11: com cliente vinculado, e-mail e WhatsApp usam o dado da ficha.
  const contato = contatoDoLead(lead);
  // Registrar perda so faz sentido para lead aberto, e depende de lead:perder
  // — nao de lead:edit (antes ficava escondido dentro do bloco de edicao).
  const podeRegistrarPerdaAqui = podeRegistrarPerda && !encerrado;
  const temAcaoRapida = podeEditar || podeRegistrarPerdaAqui || Boolean(lead.cliente_resumo);

  return (
    <PageContainer>
      <CockpitHeader
        lead={lead}
        etapaNome={etapaNome}
        onVoltar={voltar}
        onCriarOrcamento={() => setDialog("orcamento")}
        onMoverEtapa={() => setDialog("mover")}
        onArquivar={handleArquivar}
        onExcluir={handleExcluir}
        onReativarPerda={handleReativar}
        podeExcluir={podeExcluir}
        podeRegistrarPerda={podeRegistrarPerda}
        podeCriarOrcamento={podeCriarOrcamento}
        podeEditar={podeEditar}
        podeMoverEtapa={podeMover && !encerrado}
      />

      <CockpitKpis
        metricas={lead.metricas}
        valorEstimado={lead.valor_estimado}
        orcamentos={lead.orcamentos}
      />

      <CockpitGrid>
        {/* ---- Coluna esquerda (acoes + dados + responsavel), sticky ---- */}
        <LeftRail>
          {temAcaoRapida && (
            <Block title="Ações rápidas">
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {podeEditar && (
                  <>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => setDialog("atividade")}
                      style={{ justifyContent: "flex-start" }}
                    >
                      <FiPhone size={14} aria-hidden="true" /> Registrar contato
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => {
                        const url = linkWhatsapp(contato.telefone);
                        if (url) window.open(url, "_blank", "noopener,noreferrer");
                      }}
                      // Sem link = telefone ausente ou incompleto (sem DDD):
                      // abrir wa.me com numero que nao existe era o bug do 55.
                      disabled={!linkWhatsapp(contato.telefone)}
                      title={
                        linkWhatsapp(contato.telefone)
                          ? "Abrir conversa no WhatsApp"
                          : contato.telefone
                            ? "Telefone incompleto: confira o DDD"
                            : contato.fonte === "cliente"
                              ? "Cliente sem telefone cadastrado"
                              : "Lead sem telefone cadastrado"
                      }
                      style={{ justifyContent: "flex-start" }}
                    >
                      <FiMessageCircle size={14} aria-hidden="true" /> Enviar WhatsApp
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => setDialog("email")}
                      style={{ justifyContent: "flex-start" }}
                    >
                      <FiMail size={14} aria-hidden="true" /> Enviar e-mail
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => setDialog("compromisso")}
                      style={{ justifyContent: "flex-start" }}
                    >
                      <FiCalendar size={14} aria-hidden="true" /> Novo compromisso
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => setDialog("formulario")}
                      disabled={encerrado}
                      title={
                        encerrado
                          ? "Lead encerrado: o cliente não completa mais o cadastro"
                          : "Gerar link pessoal do formulário de enriquecimento"
                      }
                      style={{ justifyContent: "flex-start" }}
                    >
                      <FiSend size={14} aria-hidden="true" /> Enviar formulário
                    </button>
                  </>
                )}
                {lead.cliente_resumo && (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() =>
                      navigate(`/clientes/${lead.cliente_resumo.id}`, {
                        state: { from: `/crm/leads/${leadId}` },
                      })
                    }
                    style={{ justifyContent: "flex-start" }}
                  >
                    <FiUser size={14} aria-hidden="true" /> Ver ficha do cliente
                  </button>
                )}
                {podeRegistrarPerdaAqui && (
                  <button
                    type="button"
                    className="btn btn--danger"
                    onClick={() => setDialog("perda")}
                    style={{ justifyContent: "flex-start" }}
                  >
                    <FiXCircle size={14} aria-hidden="true" /> Registrar perda
                  </button>
                )}
              </div>
            </Block>
          )}

          <DadosLeadBlock
            lead={lead}
            onSalvar={handleSalvarDados}
            podeEditar={podeEditar}
            request={request}
            leadId={leadId}
            onParceirosChange={carregar}
            onAbrirCliente={
              lead.cliente_resumo
                ? () =>
                    navigate(`/clientes/${lead.cliente_resumo.id}`, {
                      state: { from: `/crm/leads/${leadId}` },
                    })
                : undefined
            }
          />

          <ResponsavelBlock
            lead={lead}
            onAtribuir={handleAtribuir}
            podeEditar={podeEditar}
          />
        </LeftRail>

        {/* ---- Coluna direita (secoes de conteudo, colapsaveis) ---- */}
        <MainCol>
          <OrcamentosBlock
            orcamentos={lead.orcamentos}
            onCriarOrcamento={() => setDialog("orcamento")}
            podeCriar={podeCriarOrcamento}
            collapsible
            defaultCollapsed={false}
          />

          <CompromissosBlock
            compromissos={lead.compromissos}
            onNovo={() => {
              setCompromissoEmEdicao(null);
              setDialog("compromisso");
            }}
            onEditar={abrirRemarcar}
            onRealizar={handleRealizarCompromisso}
            onExcluir={handleExcluirCompromisso}
            podeEditar={podeEditar}
            collapsible
            defaultCollapsed
          />

          <TarefasBlock
            tarefas={lead.tarefas}
            onCriarTarefa={handleCriarTarefa}
            onToggleTarefa={handleToggleTarefa}
            onAtualizarTarefa={handleAtualizarTarefa}
            onExcluirTarefa={handleExcluirTarefa}
            onCriarSubtarefa={handleCriarSubtarefa}
            onToggleSubtarefa={handleToggleSubtarefa}
            onAtualizarSubtarefa={handleAtualizarSubtarefa}
            onExcluirSubtarefa={handleExcluirSubtarefa}
            podeEditar={podeEditar}
            responsavelPadrao={lead.responsavel || ""}
            collapsible
            defaultCollapsed
          />

          <DiagnosticoSpin
            spin={lead.spin}
            completude={lead.metricas?.spin_completude}
            onAdicionarNota={handleAdicionarNotaSpin}
            onExcluirNota={handleExcluirNota}
            podeEditar={podeEditar}
            collapsible
            defaultCollapsed
          />

          <AnexosBlock
            leadId={leadId}
            podeEditar={podeEditar}
            collapsible
            defaultCollapsed
          />

          <RespostasFormularioBlock
            leadId={leadId}
            versao={respostasVersao}
            collapsible
            defaultCollapsed
          />

          <NotasGerais
            notas={lead.spin?.GERAL}
            onAdicionar={handleAdicionarNotaGeral}
            onEditar={handleEditarNota}
            onFixar={handleFixarNota}
            onExcluir={handleExcluirNota}
            podeEditar={podeEditar}
            collapsible
            defaultCollapsed
          />

          <TimelineHistorico
            timeline={timeline}
            podeComentar={podeEditar}
            onComentar={handleComentarTimeline}
            collapsible
            defaultCollapsed
          />
        </MainCol>
      </CockpitGrid>

      {/* ---- Dialogs ---- */}
      <RegistrarAtividadeDialog
        open={dialog === "atividade"}
        onClose={fecharDialog}
        onRegistrar={handleRegistrarAtividade}
        subtitle={lead.numero || ""}
      />
      <EnviarFormularioDialog
        open={dialog === "formulario"}
        onClose={fecharDialog}
        leadId={leadId}
        telefone={contato.telefone}
        podePreencher={podePreencherFormulario}
        onRespondido={() => {
          setRespostasVersao((v) => v + 1);
          carregar();
        }}
      />
      <EnviarEmailDialog
        open={dialog === "email"}
        onClose={fecharDialog}
        onEnviar={handleEnviarEmail}
        email={contato.email}
        sugestao={contextoSpin}
      />
      <NovoCompromissoDialog
        open={dialog === "compromisso"}
        onClose={() => {
          setCompromissoEmEdicao(null);
          fecharDialog();
        }}
        onCriar={handleCriarCompromisso}
        onSalvar={handleAtualizarCompromisso}
        compromisso={compromissoEmEdicao}
        responsavelPadrao={lead.responsavel || ""}
        subtitle={lead.numero}
      />
      <CriarOrcamentoDialog
        open={dialog === "orcamento"}
        onClose={fecharDialog}
        onCriar={handleCriarOrcamento}
        lead={lead}
        contextoSpin={contextoSpin}
      />
      <MoverEtapaDialog
        open={dialog === "mover"}
        onClose={fecharDialog}
        onMover={handleMover}
        lead={lead}
      />
      <RegistrarPerdaDialog
        open={dialog === "perda"}
        onClose={fecharDialog}
        onPerder={handlePerder}
        subtitle={lead.numero || ""}
      />
    </PageContainer>
  );
}

export default LeadCockpit;
